import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../Reducer/SnackbarReducer';
import useActionLog from './useActionLog';
import { closeLoading, showLoading } from '../Reducer/LoadingReducer';
import { useSchemaValidation } from './useSchema';
import { SAVE_MESSAGE_PRESET } from '../Presets/SaveMessagePreset';

const toRows = (value, schemaFields) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value];
  const keys = Object.keys(schemaFields || {});
  if (keys.length === 1) return [{ [keys[0]]: value }];
  return [{}];
};

export function useSave({
  menuId,
  action = null,
  schema = null,
  saveData,
  requestSave,
  fileSave = null,
  onSuccess = null,
  showSuccess = true,
}) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const [detailData, setDetailData] = useState(null);
  const { SaveActionLog } = useActionLog();
  const { validateRequiredWithMessage } = useSchemaValidation();

  const run = async (param) => {
    const getMessages = () => {
      const menu = SAVE_MESSAGE_PRESET[menuId] || SAVE_MESSAGE_PRESET.default;

      //프리셋 결정 로직:
      // - action이 있고 menu[action]이 존재하면 해당 액션 프리셋 사용
      // - 그 외의 모든 경우(단일 메뉴인데 문구가 없거나, action이 없거나 등)는
      //   해당 메뉴의 success/fail 필드를 직접 보거나, 없으면 전체 default 사용
      const preset = action && menu[action] ? menu[action] : menu;

      return {
        success: preset.success || SAVE_MESSAGE_PRESET.default.success,
        fail: preset.fail || SAVE_MESSAGE_PRESET.default.fail,
      };
    };

    const msgs = getMessages();

    dispatch(showLoading());

    const schemaFields = schema?.fields ? schema.fields : schema || {};
    const base = saveData ? saveData(param) : param;
    let requestRows = toRows(base, schemaFields);

    try {
      // 유효성 검사
      if (!validateRequiredWithMessage(requestRows, schema)) return;

      // 파일 저장 로직
      if (fileSave) {
        const filePatch = await fileSave(requestRows, param);
        requestRows = requestRows.map((r) => ({ ...r, ...filePatch }));
      }

      const saveResponse = await requestSave?.(requestRows);

      let saveDetailData = [];
      if (saveResponse) {
        if (saveResponse.data) {
          saveDetailData = Array.isArray(saveResponse.data)
            ? saveResponse.data
            : [saveResponse.data];
        } else if (Array.isArray(saveResponse)) {
          saveDetailData = saveResponse;
        } else {
          saveDetailData = [saveResponse];
        }
      }

      setDetailData(saveDetailData.length ? saveDetailData : null);

      const hasFailure = (saveDetailData ?? []).some(
        (r) => r.isSuccess === false
      );

      if (hasFailure) {
        console.error(
          `[Save Failure - ${menuId}${action ? ':' + action : ''}]`,
          saveDetailData[0]
        );

        dispatch(
          showSnackbar({
            message: msgs.fail,
            type: 'error',
          })
        );
        return;
      } else {
        if (showSuccess) {
          dispatch(showSnackbar({ message: msgs.success, type: 'success' }));
        }
      }

      await onSuccess?.({ saveDetailData, requestRows, userInfo });

      await SaveActionLog(requestRows, {
        menuId,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: hasFailure ? 'ERROR' : 'SUCCESS',
      });
    } catch (e) {
      console.error(`[Runtime Error - ${menuId}]`, e);

      dispatch(
        showSnackbar({
          message: msgs.fail,
          type: 'error',
        })
      );

      await SaveActionLog(requestRows, {
        menuId,
        coCd: userInfo.authCd === 'ADMIN' ? '' : userInfo.coCd,
        userId: userInfo.userid,
        logResult: 'ERROR',
        errorMsg: e?.message,
      });
    } finally {
      dispatch(closeLoading());
    }
  };

  return { run, detailData };
}
